import { useState } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Mousewheel } from 'swiper/modules';
import type { Swiper as SwiperClass } from 'swiper';
import 'swiper/css';
import ConceptCard from '../ConceptCard';
import { PRODUCTS, type ProductData } from '../../config';
import Arrow from '@/components/Arrow';
import playIcon from '@/assets/svg/play.png';
import useScrollReveal from '@/hooks/useScrollReveal';
import styles from './index.module.scss';

const VISIBLE_COUNT = 3;
/** 切换动效时长（ms） */
const SPEED = 320;

export default function ProductShowcase() {
  const { ref, revealProps } = useScrollReveal<HTMLElement>();
  const [activeKey, setActiveKey] = useState(PRODUCTS.defaultKey);
  const [playing, setPlaying] = useState(false);
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const product: ProductData = PRODUCTS.items[activeKey];
  const concepts = product.concepts;
  const showArrows = concepts.length > VISIBLE_COUNT;

  const handleTabChange = (key: string) => {
    setActiveKey(key);
    setPlaying(false);
  };

  const handleHeroClick = () => setPlaying(true);

  return (
    <section ref={ref} className={styles.section}>
      <div className={styles.bgMedia}>
        <img src={PRODUCTS.background} alt="" loading="lazy" />
      </div>
      <div className={styles.inner} {...revealProps}>
        <div className={styles.intro}>
          <div className={styles.header}>
            <h2 className={styles.title}>{PRODUCTS.title}</h2>
            <p className={styles.subtitle}>{PRODUCTS.subTitle}</p>
          </div>

          <div
            className={styles.tabs}
            style={{ '--tab-index': PRODUCTS.tabOrder.indexOf(activeKey) } as React.CSSProperties}
          >
            <span className={styles.tabIndicator} />
            {PRODUCTS.tabOrder.map((key) => {
              const item = PRODUCTS.items[key];
              const active = key === activeKey;
              return (
                <button
                  key={key}
                  type="button"
                  className={`${styles.tab} ${active ? styles.tabActive : ''}`}
                  onClick={() => handleTabChange(key)}
                >
                  {item.tab}
                </button>
              );
            })}
          </div>
        </div>

        <div className={styles.detailCard}>
          <div className={styles.detailText}>
            <div className={styles.detailTitle}>
              <h3 className={styles.productName}>{product.title}</h3>
              <p className={styles.productSubtitle}>{product.subtitle}</p>
            </div>
            <div className={styles.productDesc}>{product.description}</div>
          </div>
          <div className={styles.detailImage}>
            {playing ? (
              <video
                className={styles.detailVideo}
                src={product.video}
                poster={product.hero}
                autoPlay
                controls
                playsInline
              />
            ) : (
              <button
                type="button"
                className={styles.heroTrigger}
                onClick={handleHeroClick}
                aria-label={`播放 ${product.title} 介绍视频`}
              >
                <img
                  className={styles.poster}
                  src={product.hero}
                  alt={product.title}
                  loading="lazy"
                />
                <span className={styles.play} aria-hidden>
                  <img src={playIcon} alt="" loading="lazy" />
                </span>
              </button>
            )}
          </div>
        </div>

        <div className={`${styles.conceptArea} ${showArrows ? styles.hasArrows : ''}`}>
          <h3 className={styles.conceptTitle}>{product.conceptTitle}</h3>
          <div className={styles.conceptViewport}>
            {showArrows && (
              <button
                type="button"
                className={`${styles.arrow} ${styles.arrowLeft}`}
                onClick={() => swiper?.slidePrev()}
                aria-label="上一组"
              >
                <Arrow className={styles.arrowIcon} />
              </button>
            )}
            {/* 切 Tab 时重挂载，卡片数与位置一并重置 */}
            <Swiper
              key={activeKey}
              className={styles.conceptTrackWrap}
              modules={[Mousewheel]}
              slidesPerView={VISIBLE_COUNT}
              slidesPerGroup={1}
              speed={SPEED}
              loop={showArrows}
              allowTouchMove
              mousewheel={showArrows && { forceToAxis: true, releaseOnEdges: true }}
              onSwiper={setSwiper}
            >
              {concepts.map((item) => (
                <SwiperSlide key={item.title} className={styles.conceptSlot}>
                  <ConceptCard {...item} />
                </SwiperSlide>
              ))}
            </Swiper>
            {showArrows && (
              <button
                type="button"
                className={`${styles.arrow} ${styles.arrowRight}`}
                onClick={() => swiper?.slideNext()}
                aria-label="下一组"
              >
                <Arrow className={styles.arrowIcon} />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
